<template>
  <section class="invoice-preview-wrapper">
    <b-breadcrumb class="mb-2">
      <b-breadcrumb-item :to="{name:'home'}">
        <p class="d-flex align-items-center">
          <feather-icon
            icon="HomeIcon"
            size="18"
            class="cursor-pointer"
          />
          <span style="margin-left: 0.5rem">Home</span>
        </p>
      </b-breadcrumb-item>

      <b-breadcrumb-item :to="{name:'order'}">
        Orders
      </b-breadcrumb-item>

      <b-breadcrumb-item active>
        {{ $route.params.id }}
      </b-breadcrumb-item>
    </b-breadcrumb>

    <b-overlay
      :show="isLoading"
      rounded
      variant="transparent"
      spinner-variant="primary"
    >
      <template v-if="keys(order).length > 1">
        <b-row class="invoice-preview">
          <!-- Col: Left (Invoice Container) -->
          <b-col
            cols="12"
            xl="9"
            md="8"
          >
            <b-card
              no-body
              class="invoice-preview-card"
            >
              <!-- Header -->
              <b-card-body class="invoice-padding pb-0">

                <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

                  <!-- Header: Left Content -->
                  <div>
                    <div class="logo-wrapper">
                      <h3 class="text-primary invoice-logo">
                        Order #{{ order.id }}
                      </h3>
                    </div>
                    <p class="card-text mb-25 text-bold h4">
                      <span class=" text-dark">Order time: </span>
                      <span class="text-warning">{{ orderTime }}</span>
                    </p>
                  </div>

                  <!-- Header: Right Content -->
                  <div class="mt-md-0">
                    <h4
                      class="invoice-title"
                    >
                      <span
                        style="padding:0.5rem 1rem;border-radius: 0.75rem;display: inline-block"
                        :class="statuses[order.state].class"
                        class="text-white bg-darken-2"
                      >
                        {{ statuses[order.state].value }}
                      </span>
                    </h4>
                  </div>
                </div>
              </b-card-body>

              <!-- Spacer -->
              <hr class="invoice-spacing">

              <!-- Invoice Client & Payment Details -->
              <b-card-body
                class="invoice-padding pt-0"
              >
                <b-row class="invoice-spacing">

                  <!-- Col: Invoice To -->
                  <b-col
                    cols="12"
                    xl="6"
                    class="p-0"
                  >
                    <h6 class="mb-2">
                      Client:
                    </h6>
                    <h6 class="mb-25">
                      {{ order.user.name }}
                    </h6>
                    <p class="card-text mb-25">
                      {{ order.user.phone }}
                    </p>
                  </b-col>

                  <!-- Col: Payment Details -->
                  <b-col
                    xl="6"
                    cols="12"
                    class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end"
                  >
                    <div>
                      <h6 class="mb-2">
                        Payment Details:
                      </h6>
                      <table>
                        <tbody>
                          <tr>
                            <td class="pr-1">
                              Platform:
                            </td>
                            <td>{{ order.payment.platform }}</td>
                          </tr>
                          <tr>
                            <td class="pr-1">
                              Amount:
                            </td>
                            <td><span class="font-weight-bold">{{ order.payment.amount }}</span></td>
                          </tr>
                          <tr>
                            <td class="pr-1">
                              Total:
                            </td>
                            <td>{{ order.total }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Right Col: Card -->
          <b-col
            cols="12"
            md="4"
            xl="3"
            class="invoice-actions"
          >
            <b-card>

              <!-- Button: Send Invoice -->
              <b-button
                variant="primary"
                class="mb-75"
                block
                @click="showModal = true"
              >
                Change Status
              </b-button>

              <b-button
                variant="outline-primary"
                class="mb-75"
                block
                @click="openReceptionPage"
              >
                Reception
              </b-button>

              <!-- Button: Print -->
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                variant="outline-secondary"
                class="mb-75"
                block
                @click="printInvoice"
              >
                Print
              </b-button>

            </b-card>
          </b-col>
        </b-row>

        <b-table-lite
          responsive
          :items="order.products"
          :fields="productsFields"
        >
          <template #cell(id)="data">
            {{ data.item.product.id }}
          </template>

          <template #cell(image)="data">
            <b-img
              :src="data.item.product.image"
              alt="upload image"
              style="width: 6rem;height: 6rem;object-fit: cover"
              rounded
            />
          </template>

          <template #cell(name)="data">
            {{ data.item.product.name }}
          </template>

          <template #cell(quantity)="data">
            {{ data.item.count }}
          </template>

          <template #cell(warehouse)="data">
            {{ data.item.warehouse.name }}
          </template>
        </b-table-lite>

        <change-status-sidebar
          :show="showModal"
          :state="order.state"
          @update:show="showModal = false"
          @update:data="fetchDetails"
        />
      </template>

    </b-overlay>
  </section>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  BRow, BCol, BCard, BCardBody, BTableLite, BButton, VBToggle, BImg, BOverlay, BBreadcrumbItem, BBreadcrumb,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import useOrder from '@/composables/useOrder'
// eslint-disable-next-line import/no-cycle
import router from '@/router'
import { keys } from '@/util/object'
import ChangeStatusSidebar from '@/views/order/components/ChangeStatusSidebar.vue'
import useLoading from '@/composables/useLoading'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BBreadcrumb,
    BBreadcrumbItem,
    BOverlay,
    BImg,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BButton,
    ChangeStatusSidebar,
  },
  methods: { keys },
  setup() {
    const { statuses, findOne } = useOrder()
    const { isLoading, startLoading, finishLoading } = useLoading()
    const order = ref({
      products: [],
    })
    const showModal = ref(false)

    const orderTime = computed(() => {
      if (keys(order.value).length > 0 && order.value.created_at) {
        return order.value?.created_at.replaceAll('-', '/')
      }

      return ''
    })

    const productsFields = [
      { key: 'id', label: '#' },
      { key: 'image', label: 'Image' },
      { key: 'name', label: 'Name' },
      { key: 'quantity', label: 'Quantity' },
      { key: 'size', label: 'Size' },
      { key: 'price', label: 'Price' },
      { key: 'warehouse', label: 'Warehouse' },
    ]

    const printInvoice = () => {
      window.print()
    }

    async function fetchDetails() {
      try {
        startLoading()
        const response = await findOne({
          id: router.currentRoute.params.id,
        })

        order.value = response.data.document
      } finally {
        finishLoading()
      }
    }

    function openReceptionPage() {
      router.push({
        name: 'reception',
        params: {
          id: order.value.id,
        },
      })
    }

    fetchDetails()

    return {
      productsFields,
      order,
      showModal,
      statuses,
      orderTime,
      isLoading,
      printInvoice,
      fetchDetails,
      openReceptionPage,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {

  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
